import React, { FC, useEffect, useState, useRef } from 'react';

import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';

import Text from '~components/atoms/Text';
import Modal from '~components/atoms/Modal';
import ActionButton from '~components/atoms/ActionButton';

import BuyOrSellGoldSummary from '../BuyOrSellGoldSummary';
import GoldTransactionForm from '../GoldTransactionForm';

import { ModuleEnum, PermissionsEnum, TransactionActionEnum } from '~utils/enums';

import * as Styles from './_buyOrSellGoldModal.module.scss';

type BuyOrSellGoldModalProps = {
    isOpen: boolean;
    onClose: () => void;
    transactionType: TransactionActionEnum;
}

const BuyOrSellGoldModal: FC<BuyOrSellGoldModalProps> = ({
    isOpen,
    onClose,
    transactionType,
}) => {
    const dispatch = useAppDispatch();
    const setIsConfirmationModalOpen = (open: boolean) => {
        dispatch(combinedActions.goldSetIsConfirmationModalOpen(open));
    };
    const setIsBuyOrSellGoldModalOpen = (open: boolean) => {
        dispatch(combinedActions.goldSetIsBuyOrSellGoldModalOpen(open));
    };

    const isFormFilled = useAppSelector((state) => state.gold.modals.isFormFilled);
    const isConfirmationModalOpen = useAppSelector((state) => state.gold.modals.isConfirmationModalOpen);

    const createAccessModules = useAppSelector(state => state.auth.userDetails.access).find(access => access.permission === PermissionsEnum.Create)?.modules || [];

    const [timeLeft, setTimeLeft] = useState(300); // 5 minutes
    const [isReminderTriggered, setIsReminderTriggered] = useState(false);
    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const resetAllCustomersList = () => dispatch(combinedActions.customerResetAllCustomersState());

    // Clear timer interval on component unmount
    useEffect(() => {
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }
        };
    }, []);

    // Handle modal open/close
    useEffect(() => {
        if (createAccessModules.find(module => module === ModuleEnum.BuySellGold)) {
            if (!isOpen) {
                dispatch(combinedActions.goldClearSellOrBuyGoldParams());
                dispatch(combinedActions.goldClearInventoryFilterParams());
                dispatch(combinedActions.goldClearSerialNumberFilterParams());
                dispatch(combinedActions.goldGetAllInventoriesAttempt());
                dispatch(combinedActions.goldSetIsFormFilled(false));
                resetAllCustomersList();

                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                    timerIntervalRef.current = null;
                }

                setTimeLeft(300); // Reset timer state when modal closes
            } else {
                dispatch(combinedActions.goldGetGoldPricesAttempt());
                dispatch(combinedActions.silverGetSilverPricesAttempt());

                // Clear any existing interval before starting a new one
                if (timerIntervalRef.current) {
                    clearInterval(timerIntervalRef.current);
                }

                setTimeLeft(300);
                startTimer();
            }
        }
    }, [isOpen]);

    useEffect(() => {
        if (timeLeft === 60) {
            setIsReminderTriggered(true);
            setIsConfirmationModalOpen(true);
        }

        if (timeLeft === 0) {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
            }

            setIsConfirmationModalOpen(true);
        }
    }, [timeLeft]);

    const startTimer = () => {
        // Clear any existing interval before starting a new one
        if (timerIntervalRef.current) {
            clearInterval(timerIntervalRef.current);
        }

        timerIntervalRef.current = setInterval(() => {
            setTimeLeft((currentTime) => {
                if (currentTime <= 0) {
                    if (timerIntervalRef.current) {
                        clearInterval(timerIntervalRef.current);
                    }
                    return 0;
                }
                return currentTime - 1;
            });
        }, 1000);
    };

    const renderTimer = () => (
        <div className={Styles.timer}>
            {`${Math.floor(timeLeft / 60)}:${(timeLeft % 60).toString().padStart(2, '0')}`}
        </div>
    );

    const renderContent = () => {
        if (isFormFilled) {
            return <BuyOrSellGoldSummary transactionType={transactionType} />;
        }

        return <GoldTransactionForm />;
    };

    const renderConfirmationModalContent = () => {
        if (timeLeft <= 60 && timeLeft > 0 && isReminderTriggered) {
            return (
                <Text label='You have 60 seconds left before the transaction expires!' isError />
            );
        }

        if (timeLeft === 0) {
            return (
                <Text label='You have exceeded the time limit. Please try again.' isError />
            );
        }

        if (!isReminderTriggered) {
            return (
                <>
                    <Text label='Are you sure you want to stop the transaction? You still have time left.' isError />

                    <div className={Styles['button-container']}>
                        <ActionButton
                            label='Cancel'
                            onClick={() => setIsConfirmationModalOpen(false)}
                        />

                        <ActionButton
                            label='Confirm'
                            onClick={() => {
                                setIsConfirmationModalOpen(false);
                                setIsBuyOrSellGoldModalOpen(false);
                            }}
                        />
                    </div>
                </>
            );
        }
    };

    const handleCloseConfirmationModal = () => {
        if (timeLeft > 0) {
            setIsReminderTriggered(false);
            setIsConfirmationModalOpen(false);
            return;
        }

        setIsConfirmationModalOpen(false);
        setIsBuyOrSellGoldModalOpen(false);
        resetAllCustomersList();
    };

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={`${TransactionActionEnum[transactionType].split(/(?=[A-Z])/).join(' ')} Gold Transaction Details`}
                timer={renderTimer()}
            >
                {renderContent()}
            </Modal>

            <Modal
                isOpen={isConfirmationModalOpen}
                onClose={handleCloseConfirmationModal}
                title='Warning!'
            >
                {renderConfirmationModalContent()}
            </Modal>
        </>
    );
};

export default BuyOrSellGoldModal;
